import { AdjustmentType, RoundingMethod, RoundingType } from '@weareopenr/types';
import { IPriceBandFields } from 'src/types/priceRules';
import { formatPrice, formatPriceValue } from 'src/helpers/formatPrice/formatPrice';
import { IPriceRule } from 'src/types/price.types';

export type TPriceRuleTitleStep = {
  title: string;
  subtitle: string;
};

export function getTitles(step: number, steps: TPriceRuleTitleStep[]) {
  const { title = '', subtitle = '' } = steps[step] ?? {};
  return [title, subtitle];
}

export const buildCalculationSubtitle = (calculation: IPriceBandFields['adjustment']) => {
  if (calculation.type === AdjustmentType.NONE) {
    return 'No calculation';
  }

  if (calculation.type === AdjustmentType.FIXED) {
    return `Adjust by fixed amount ${formatPrice(Number(calculation.fixed))}`;
  } else {
    return `Adjust by percentage amount ${Number(calculation.percentage)}%`;
  }
};

export const buildRoundingSubtitle = (rounding: IPriceBandFields['rounding']) => {
  if (rounding.type === RoundingType.NONE) {
    return 'No rounding';
  }

  const value = Number(rounding.value);
  if (isNaN(value)) {
    return 'Invalid rounding';
  }

  const str: string[] = [];
  switch (rounding.type) {
    case RoundingType.UP:
      str.push('Round up to');
      break;
    case RoundingType.DOWN:
      str.push('Round down to');
      break;
    case RoundingType.NEAREST:
      str.push('Round to nearest');
      break;
  }

  if (rounding.method === RoundingMethod.INTERVAL) {
    str.push('interval of');
  }

  str.push(formatPrice(value));
  return str.join(' ');
};

const ROUNDING_LABEL: Partial<Record<RoundingType, string>> = {
  [RoundingType.UP]: 'Rounding up to',
  [RoundingType.DOWN]: 'Rounding down to',
  [RoundingType.NEAREST]: 'Rounding to nearest',
};

export const buildRoundingSuffix = (rule: IPriceRule): string => {
  const { type, method, value } = rule.rounding ?? {};
  const label = type ? ROUNDING_LABEL[type] : undefined;
  if (!label) return '';
  const parts = [label];
  if (value !== undefined) parts.push(String(value));
  if (method) parts.push(`(${method.toLowerCase()})`);
  return `, ${parts.join(' ')}`;
};

export const buildExistingRuleSubtitle = (rule: IPriceRule, noRuleText: string): string => {
  const { type, value } = rule.adjustment;
  const hasAdjustment = type !== AdjustmentType.NONE;
  const hasRounding = rule.rounding?.type !== RoundingType.NONE;

  if (!hasAdjustment && !hasRounding) return noRuleText;

  const parts: string[] = [];
  if (hasAdjustment) {
    parts.push(type === AdjustmentType.FIXED ? `${formatPriceValue(value)} (£/€)` : `${formatPriceValue(value)}%`);
  } else {
    parts.push('No calculation');
  }
  if (hasRounding) {
    parts.push(buildRoundingSuffix(rule).replace(/^, /, ''));
  } else {
    parts.push('No Rounding');
  }
  return parts.join(', ');
};
